/**
 * ChecklistsTab.js
 *
 * Hybrid layout: Category cards + Quick Start + Recent
 * NO PERMITS - they will be a separate module
 */

import React, { useState, useMemo, useCallback } from 'react';
// FT-357: shell-level back handler — bottom bar owns Back for sub-views.
import { useSetBackHandler } from '../../../../contexts/NavigationBackContext';
// FT-358: middle-slot actions registered via context; the shell owns the bar.
import { useSetBottomBarActions } from '../../../../contexts/MobileBottomBarActionsContext';
import {
  AlertTriangle,
  Anchor,
  Users,
  Compass,
  Settings,
  Calendar,
  Clock,
  PlusCircle,
  CheckCircle,
  ChevronRight,
  ChevronLeft,
  History,
  Search,
  Sun,
  Waves,
  Clipboard,
  FolderOpen
} from 'lucide-react';

// Config
import { CHECKLIST_TYPE_CONFIG } from '../../../../config/operations/checklistImportConfig';
import { getChecklistsForCategory } from '../../../../config/operations/checklistDefinitions';

// Hooks
import { useDailyChecks } from '../../../../hooks/domain/operations/useDailyChecks';
// FT-324 cycle 3: read the eagerly-mounted single instance from context
// (mounted at OperationsModule level), NOT a fresh local instance.
import { useChecklistRecordsContext } from '../../../../hooks/domain/operations/ChecklistRecordsContext';
import useOperationalChecklists from '../../../../hooks/domain/operations/useOperationalChecklists';
import { useChecklistCategoryVisibility } from '../../../../hooks/domain/operations/useChecklistCategoryVisibility';
import { useChecklistConfig } from '../../../../hooks/domain/operations/useChecklistConfig';
import { useMainLogbook } from '../../../../hooks/domain/operations/useMainLogbook';

// Settings component
import ChecklistSettingsTab from '../../settings/tabs/ChecklistSettingsTab';

// Execution components (existing)
import DailyCheckExecutionView from './DailyCheckExecutionView';
import CompletedChecklistView from './CompletedChecklistView';
import GenericChecklistExecutionView from './GenericChecklistExecutionView';

// FT-310: browsable historical checklists view (subtab)
import ChecklistHistoryTab from './ChecklistHistoryTab';

// Icon mapping for categories
const CATEGORY_ICONS = {
  emergency: AlertTriangle,
  pre_departure: Anchor,
  daily: Calendar,
  navigation: Compass,
  operational: Settings,
  familiarisation: Users,
  periodic: Clock,
  custom: PlusCircle
};

// Helper to get icon for custom categories
const getCategoryIcon = (categoryId) => {
  if (categoryId?.startsWith('custom_')) return FolderOpen;
  return CATEGORY_ICONS[categoryId] || Settings;
};

// Color mapping for categories
const CATEGORY_COLORS = {
  emergency: { bg: 'bg-red-100', text: 'text-red-600', border: 'border-red-200', hover: 'hover:bg-red-50' },
  pre_departure: { bg: 'bg-blue-100', text: 'text-blue-600', border: 'border-blue-200', hover: 'hover:bg-blue-50' },
  daily: { bg: 'bg-slate-100', text: 'text-slate-600', border: 'border-slate-200', hover: 'hover:bg-slate-50' },
  navigation: { bg: 'bg-cyan-100', text: 'text-cyan-600', border: 'border-cyan-200', hover: 'hover:bg-cyan-50' },
  operational: { bg: 'bg-amber-100', text: 'text-amber-600', border: 'border-amber-200', hover: 'hover:bg-amber-50' },
  familiarisation: { bg: 'bg-green-100', text: 'text-green-600', border: 'border-green-200', hover: 'hover:bg-green-50' },
  periodic: { bg: 'bg-purple-100', text: 'text-purple-600', border: 'border-purple-200', hover: 'hover:bg-purple-50' },
  custom: { bg: 'bg-indigo-100', text: 'text-indigo-600', border: 'border-indigo-200', hover: 'hover:bg-indigo-50' }
};

/**
 * FT-322 — belt-and-braces auto-log helper for non-daily checklist completions.
 *
 * Called from the GenericChecklistExecutionView `onComplete` handler in this
 * tab. Mirrors the FT-309 pattern used for daily checks: on completion, write
 * a Main-Logbook entry so non-daily checklists (Watch Handover, Heavy Weather,
 * Anchoring, etc.) surface in the logbook.
 *
 * Idempotence — GenericChecklistExecutionView's own handleSubmit ALREADY
 * calls logChecklistCompletion internally (gated on
 * `linkedContext?.autoLogToLogbook !== false`). If we simply added a second
 * outer call, every completion would create two logbook rows. We dedupe by
 * inspecting the current `logbookEntries` array: if any entry's `sourceId`
 * already equals `record.id` (which is what the inner call writes), we skip.
 *
 * Extracted as a pure helper so it can be unit-tested without mounting the
 * entire ChecklistsTab tree with all its context providers.
 *
 * @param {Object} params
 * @param {Object} params.record  - completion record from GenericChecklistExecutionView
 * @param {Array}  params.logbookEntries - current Main-Logbook entries (for dedupe)
 * @param {Function} params.logChecklistCompletion - useMainLogbook auto-log method
 * @returns {boolean} true if a new logbook entry was written, false if deduped/skipped
 */
export const autoLogNonDailyChecklistCompletion = ({
  record,
  logbookEntries,
  logChecklistCompletion
}) => {
  if (!record || typeof logChecklistCompletion !== 'function') {
    return false;
  }
  const alreadyLogged = record?.id
    && Array.isArray(logbookEntries)
    && logbookEntries.some(e => e?.sourceId === record.id);
  if (alreadyLogged) {
    return false;
  }
  logChecklistCompletion({
    id: record?.id,
    checklistId: record?.checklistId,
    checklistName: record?.checklistName,
    checklistType: record?.checklistType,
    checks: record?.checks || [],
    completedAt: record?.completedAt,
    completedBy: record?.completedBy,
    notes: record?.notes || '',
    linkedContext: record?.linkedContext || null
  });
  return true;
};

/**
 * Category Card Component
 *
 * Gemini Bookshelf Design (2026-04-30):
 * - White bg with shadow instead of colored borders
 * - Consistent sky-50/sky-600 icon styling
 * - Emergency category retains red accent
 */
const CategoryCard = ({ category, onClick, statusBadge }) => {
  const Icon = CATEGORY_ICONS[category.id] || Settings;
  const isEmergency = category.id === 'emergency';

  return (
    <button
      onClick={onClick}
      className={`
        flex flex-col items-center justify-center p-3 rounded-2xl shadow-md ring-1 ring-slate-200/60 transition-all
        bg-white hover:shadow-lg
        group relative
      `}
    >
      <div className={`w-10 h-10 rounded-lg flex items-center justify-center mb-2 ${
        isEmergency ? 'bg-red-100' : 'bg-sky-50'
      }`}>
        <Icon className={`h-5 w-5 ${isEmergency ? 'text-red-600' : 'text-sky-600'}`} />
      </div>
      <span className={`text-sm font-semibold ${isEmergency ? 'text-red-700' : 'text-slate-900'}`}>
        {category.label}
      </span>
      {statusBadge && (
        <span className="bg-slate-100 text-slate-700 text-xs px-2 py-1 rounded-full mt-1">
          {statusBadge.text}
        </span>
      )}
      <ChevronRight className="absolute right-2 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-300
        opacity-0 group-hover:opacity-100 transition-opacity" />
    </button>
  );
};

/**
 * Quick Start Item Component
 */
const QuickStartItem = ({ icon: Icon, label, status, onStart }) => {
  return (
    <button
      onClick={onStart}
      className="w-full text-left flex items-center justify-between p-4 bg-white border border-slate-200 rounded-xl hover:border-cyan-400 hover:shadow-md transition-all"
    >
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-lg bg-slate-100 flex items-center justify-center">
          <Icon className="h-5 w-5 text-slate-600" />
        </div>
        <div>
          <p className="font-medium text-slate-800">{label}</p>
          {status && (
            <p className="text-sm text-slate-500">{status}</p>
          )}
        </div>
      </div>
      <ChevronRight className="h-5 w-5 text-slate-400" />
    </button>
  );
};

/**
 * Recent Completion Item
 */
const RecentItem = ({ record, onView }) => {
  const completedDate = new Date(record.completedAt);
  const isToday = completedDate.toDateString() === new Date().toDateString();
  const dateStr = isToday
    ? `Today ${completedDate.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}`
    : completedDate.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });

  return (
    <button
      onClick={() => onView(record)}
      className="w-full flex items-center justify-between p-3 hover:bg-slate-50 rounded-lg transition-colors"
    >
      <div className="flex items-center gap-3">
        <CheckCircle className="h-4 w-4 text-green-500" />
        <div className="text-left">
          <p className="text-sm font-medium text-slate-700">{record.checklistName}</p>
          <p className="text-xs text-slate-500">{dateStr} - {record.completedBy?.name}</p>
        </div>
      </div>
      <ChevronRight className="h-4 w-4 text-slate-300" />
    </button>
  );
};

/**
 * Single Checklist Card with Config Integration
 * FT-81: Loads user customizations before passing to execution
 */
const ChecklistCard = ({ checklist, categoryConfig, onSelect }) => {
  const colors = CATEGORY_COLORS[categoryConfig?.id] || CATEGORY_COLORS.daily;
  const ChecklistIcon = checklist.icon || Clipboard;
  const isPreDeparture = checklist.linkTo === 'voyage';

  // FT-81: Load user customizations for this checklist
  const { items: configuredItems } = useChecklistConfig(checklist.id, checklist.items || []);

  // Merge: use configured items if available, else use base items
  const mergedChecklist = {
    ...checklist,
    items: configuredItems.length > 0 ? configuredItems : (checklist.items || [])
  };

  const handleSelect = () => {
    if (checklist.comingSoon) return;
    onSelect(mergedChecklist);
  };

  // Coming soon items are not clickable
  if (checklist.comingSoon) {
    return (
      <div
        className="flex items-center justify-between p-4 bg-white border border-slate-100 rounded-xl opacity-60"
      >
        <div className="flex items-center gap-3">
          <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${colors.bg}`}>
            <ChecklistIcon className={`h-5 w-5 ${colors.text}`} />
          </div>
          <div className="text-left">
            <p className="font-medium text-slate-800">{checklist.name}</p>
            <p className="text-sm text-slate-500">{checklist.description}</p>
          </div>
        </div>
        <span className="text-xs bg-slate-100 text-slate-500 px-2 py-1 rounded">
          Coming Soon
        </span>
      </div>
    );
  }

  // Active checklists: whole row is clickable button with ChevronRight
  return (
    <button
      onClick={handleSelect}
      className={`w-full text-left flex items-center justify-between p-4 bg-white border rounded-xl hover:border-cyan-400 hover:shadow-md transition-all
        ${isPreDeparture ? 'border-blue-200 bg-blue-50/50' : 'border-slate-200'}`}
    >
      <div className="flex items-center gap-3">
        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
          isPreDeparture ? 'bg-blue-100' : colors.bg
        }`}>
          <ChecklistIcon className={`h-5 w-5 ${
            isPreDeparture ? 'text-blue-600' : colors.text
          }`} />
        </div>
        <div className="text-left">
          <p className="font-medium text-slate-800">{checklist.name}</p>
          <p className="text-sm text-slate-500">
            {checklist.description}
            {configuredItems.length !== (checklist.items?.length || 0) && (
              <span className="ml-2 text-xs text-cyan-600">
                ({configuredItems.length} items configured)
              </span>
            )}
          </p>
        </div>
      </div>
      <div className="flex items-center gap-2">
        {checklist.regulatory && (
          <span className="text-xs text-slate-400">
            {checklist.regulatory}
          </span>
        )}
        <ChevronRight className="h-5 w-5 text-slate-400" />
      </div>
    </button>
  );
};

/**
 * Category Detail View (when a category card is clicked)
 */
const CategoryDetailView = ({ category, onBack, onSelectChecklist, context, customTemplates = [] }) => {
  const Icon = CATEGORY_ICONS[category.id] || Settings;
  const colors = CATEGORY_COLORS[category.id] || CATEGORY_COLORS.daily;

  // Get base checklists for this category from definitions
  const checklists = useMemo(() => {
    return getChecklistsForCategory(category.id, customTemplates);
  }, [category.id, customTemplates]);

  // Handle checklist selection
  const handleSelect = (checklist) => {
    if (checklist.comingSoon) {
      return;
    }

    if (checklist.linkTo === 'voyage') {
      context?.onSwitchTab?.('voyage');
      return;
    }

    if (checklist.linkTo === 'dailyChecks') {
      return;
    }

    if (checklist.linkTo === 'familiarisation') {
      context?.setActiveModule?.('crew');
      return;
    }

    onSelectChecklist?.(checklist);
  };

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center gap-4 pb-4 border-b border-slate-200">
        <button
          onClick={onBack}
          className="p-2 rounded-lg hover:bg-slate-100"
        >
          <ChevronLeft className="h-5 w-5 text-slate-500" />
        </button>
        <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${colors.bg}`}>
          <Icon className={`h-6 w-6 ${colors.text}`} />
        </div>
        <div>
          <h3 className="text-lg font-semibold text-slate-800">{category.label}</h3>
          <p className="text-sm text-slate-500">{category.description}</p>
        </div>
      </div>

      {/* Checklists in this category - FT-81: Using ChecklistCard for config integration */}
      <div className="space-y-2">
        {checklists.length > 0 ? (
          checklists.map(checklist => (
            <ChecklistCard
              key={checklist.id}
              checklist={checklist}
              categoryConfig={category}
              onSelect={handleSelect}
            />
          ))
        ) : (
          <div className="text-center py-8 text-slate-500">
            <p>No checklists configured for this category yet.</p>
            <p className="text-sm mt-1">Configure in Settings tab.</p>
          </div>
        )}
      </div>
    </div>
  );
};

/**
 * Main ChecklistsTab Component
 */
const ChecklistsTab = ({ context }) => {
  // State
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedDailyCheck, setSelectedDailyCheck] = useState(null);
  const [showDailyChecksDetail, setShowDailyChecksDetail] = useState(false); // FT-80 Phase 13
  const [viewingRecord, setViewingRecord] = useState(null);
  const [executingChecklist, setExecutingChecklist] = useState(null);
  const [showSettings, setShowSettings] = useState(false);
  // FT-310: subtab switcher — 'categories' (default, existing UI) or 'history'
  // (browsable historical completions). Local state (not persisted); Jonathan
  // deferred the "remember last subtab across reloads" nice-to-have to keep
  // MVP scope tight and avoid another useStorageState surface for one bit.
  const [activeSubtab, setActiveSubtab] = useState('categories');

  // FT-357: register a shell-level back handler that dismisses the currently
  // active sub-view within this tab. Only pushes a real handler when we're in
  // a sub-view; otherwise passes `null` so the parent module's "back to
  // dashboard" handler (or whichever handler is next-down on the stack)
  // remains active. Sub-view priority (any one at a time): daily-checks
  // detail → settings → selected category.
  const isInSubView = showDailyChecksDetail || showSettings || Boolean(selectedCategory);
  const dismissSubView = useCallback(() => {
    if (showDailyChecksDetail) setShowDailyChecksDetail(false);
    else if (showSettings) setShowSettings(false);
    else if (selectedCategory) setSelectedCategory(null);
  }, [showDailyChecksDetail, showSettings, selectedCategory]);
  useSetBackHandler(isInSubView ? dismissSubView : null);

  // FT-358: register middle-slot bottom-bar actions per brief §6
  // (Checklists tab: [+ New Chk.] [History]). Silent no-op when any
  // sub-view (category detail / execution / settings / daily-checks) is
  // active. New Chk. returns user to the category picker; History
  // toggles the subtab.
  const isInExecution =
    Boolean(executingChecklist) || Boolean(viewingRecord);
  useSetBottomBarActions(
    isInSubView || isInExecution
      ? []
      : [
          {
            key: 'checklists-new',
            icon: PlusCircle,
            label: 'New Chk.',
            primary: true,
            onClick: () => setActiveSubtab('categories'),
          },
          {
            key: 'checklists-history',
            icon: Clock,
            label: 'History',
            onClick: () => setActiveSubtab('history'),
          },
        ],
  );

  // Hooks
  // FT-314: destructure completeCategory so ChecklistsTab's DailyCheckExecutionView
  // onComplete can update `sr.daily_checks.v1` — mirrors DailyChecksQuickAccess so
  // the Dashboard "Daily Checks X/Y today" badge updates from BOTH entry paths.
  const { getCategoriesWithStatus, getTodaySummary, completeCategory } = useDailyChecks();
  const { getRecentCompletions } = useChecklistRecordsContext();
  const { customTemplates } = useOperationalChecklists();
  const { getVisibleCategories } = useChecklistCategoryVisibility();
  // FT-309: daily-check auto-log / FT-322: non-daily checklist auto-log.
  // We also read `entries` so the outer FT-322 call can dedupe against any
  // logbook entry the inner GenericChecklistExecutionView already wrote for
  // this same record.id (idempotent — no duplicate logbook rows).
  const { logDailyCheck, logChecklistCompletion, entries: logbookEntries } = useMainLogbook();

  // Extract unique custom categories from templates
  const customCategories = useMemo(() => {
    const categorySet = new Set();
    customTemplates.forEach(t => {
      if (t.category?.startsWith('custom_')) {
        categorySet.add(t.category);
      }
    });
    return Array.from(categorySet).map(catId => {
      // Convert slug back to label: custom_cargo_ops -> Cargo Ops
      const label = catId.replace('custom_', '').replace(/_/g, ' ')
        .split(' ').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
      return {
        id: catId,
        label: label,
        description: 'Custom checklist category',
        icon: 'FolderOpen',
        color: 'indigo',
        isCustom: true
      };
    });
  }, [customTemplates]);

  // Data
  const dailySummary = useMemo(() => getTodaySummary(), [getTodaySummary]);
  const recentCompletions = useMemo(() => getRecentCompletions(5), [getRecentCompletions]);
  const dailyCategories = useMemo(() => getCategoriesWithStatus(), [getCategoriesWithStatus]);

  // Build category list from config - use visibility hook for dynamic filtering
  // FT-126 FIX: Removed broken VOYAGE_PHASE_IDS filter (was filtering categories, not items)
  // Voyage phase checklists (pre_departure, pre_arrival, arrival) have been removed from
  // NAVIGATION_CHECKLISTS in checklistDefinitions.js - they're now Voyage Settings only
  const categories = useMemo(() => {
    return getVisibleCategories();
  }, [getVisibleCategories]);

  // Get status badge for each category
  const getCategoryStatus = (categoryId) => {
    if (categoryId === 'daily') {
      return {
        text: `${dailySummary.completed}/${dailySummary.total} today`,
        color: dailySummary.completed === dailySummary.total ? 'text-green-600' : 'text-slate-500'
      };
    }
    if (categoryId === 'navigation') {
      // FT-126: Pre-departure is now Voyage Settings only, show count instead
      return { text: '5 types', color: 'text-cyan-500' };
    }
    if (categoryId === 'emergency') {
      return { text: '11 types', color: 'text-red-500' };
    }
    return null;
  };

  // Handle category click
  const handleCategoryClick = (category) => {
    // FT-80 Phase 13: Special handling for daily category - show Daily Checks detail
    if (category.id === 'daily') {
      setShowDailyChecksDetail(true);
      return;
    }
    // All other categories navigate to detail view, including emergency
    setSelectedCategory(category);
  };

  // Handle starting daily checks
  const handleStartDailyChecks = () => {
    const firstUncompleted = dailyCategories.find(c => !c.completed);
    if (firstUncompleted) {
      setSelectedDailyCheck(firstUncompleted);
    }
  };

  // Handle checklist selection from category detail
  const handleChecklistSelect = (checklist) => {
    if (checklist.action === 'voyage' || checklist.isPreDeparture) {
      // Pre-departure checks - switch to Voyage tab
      // This uses the context passed from OperationsModule
      if (context?.onSwitchTab) {
        context.onSwitchTab('voyage');
      }
      setSelectedCategory(null);
      return;
    }

    // If checklist has items, open execution view
    if (checklist.items && checklist.items.length > 0) {
      setExecutingChecklist(checklist);
      setSelectedCategory(null);
      return;
    }

    // Other checklists without items yet
    console.log('Selected checklist:', checklist);
  };

  // FT-80 Phase 13: If viewing Daily Checks detail view
  if (showDailyChecksDetail) {
    return (
      <div className="rounded-2xl border border-gray-200 shadow-sm bg-white overflow-hidden">
        {/* Header */}
        <div className="bg-slate-700 px-5 py-3 rounded-t-2xl">
          <div className="flex items-center gap-4">
            <button
              onClick={() => setShowDailyChecksDetail(false)}
              className="p-2 rounded-lg hover:bg-slate-500 transition-colors"
            >
              <ChevronLeft className="h-5 w-5 text-white" />
            </button>
            <div className="flex items-center gap-3">
              <Calendar className="h-5 w-5 text-white" />
              <h3 className="text-base font-semibold text-white">Daily Checks</h3>
            </div>
          </div>
        </div>

        {/* Content - List Daily Check Categories */}
        <div className="p-6 space-y-4">
          {/* Progress Summary */}
          <div className="flex items-center justify-between p-4 bg-slate-50 rounded-xl">
            <div>
              <p className="text-sm text-slate-500">Today's Progress</p>
              <p className="text-2xl font-bold text-slate-900">
                {dailySummary.completed}/{dailySummary.total}
              </p>
            </div>
            <div className={`w-16 h-16 rounded-full flex items-center justify-center ${
              dailySummary.completed === dailySummary.total
                ? 'bg-green-100 text-green-600'
                : 'bg-slate-100 text-slate-600'
            }`}>
              <span className="text-xl font-bold">{dailySummary.percentage}%</span>
            </div>
          </div>

          {/* Daily Check Categories */}
          <div className="space-y-2">
            {dailyCategories.map(cat => (
              <button
                key={cat.id}
                onClick={() => {
                  setShowDailyChecksDetail(false);  // FT-80 Phase 14: Close detail view so modal can show
                  setSelectedDailyCheck(cat);
                }}
                className={`w-full flex items-center justify-between p-4 rounded-xl border-2 transition-all ${
                  cat.completed
                    ? 'border-green-200 bg-green-50'
                    : 'border-slate-200 hover:border-slate-300'
                }`}
              >
                <div className="flex items-center gap-3">
                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                    cat.completed ? 'bg-green-100' : 'bg-slate-100'
                  }`}>
                    {cat.completed ? (
                      <CheckCircle className="h-5 w-5 text-green-600" />
                    ) : (
                      <Clipboard className="h-5 w-5 text-slate-600" />
                    )}
                  </div>
                  <div className="text-left">
                    <p className={`font-medium ${cat.completed ? 'text-green-700' : 'text-slate-800'}`}>
                      {cat.label}
                    </p>
                    <p className="text-sm text-slate-500">
                      {cat.checks?.length || 0} checks
                      {cat.completed && cat.completedAt && (
                        <span className="ml-2 text-green-600">
                          • Done at {new Date(cat.completedAt).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}
                        </span>
                      )}
                    </p>
                  </div>
                </div>
                <ChevronRight className={`h-5 w-5 ${cat.completed ? 'text-green-400' : 'text-slate-300'}`} />
              </button>
            ))}
          </div>
        </div>
      </div>
    );
  }

  // If viewing a category detail
  if (selectedCategory) {
    return (
      <div className="rounded-2xl border border-gray-200 shadow-sm bg-white overflow-hidden">
        <div className="p-6">
          <CategoryDetailView
            category={selectedCategory}
            customTemplates={customTemplates}
            onBack={() => setSelectedCategory(null)}
            onSelectChecklist={handleChecklistSelect}
            context={context}
          />
        </div>
      </div>
    );
  }

  // If viewing settings
  if (showSettings) {
    return (
      <div className="rounded-2xl border border-gray-200 shadow-sm bg-white overflow-hidden">
        {/* Settings Header */}
        <div className="bg-slate-700 px-5 py-3 rounded-t-2xl">
          <div className="flex items-center gap-4">
            <button
              onClick={() => setShowSettings(false)}
              className="p-2 rounded-lg hover:bg-slate-500 transition-colors"
            >
              <ChevronLeft className="h-5 w-5 text-white" />
            </button>
            <div className="flex items-center gap-3">
              <Settings className="h-5 w-5 text-white" />
              <h3 className="text-base font-semibold text-white">Checklist Settings</h3>
            </div>
          </div>
        </div>
        {/* Settings Content */}
        <div className="p-6">
          <ChecklistSettingsTab />
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Main Card */}
      <div className="rounded-2xl border border-gray-200 shadow-sm bg-white overflow-hidden">
        {/* Header */}
        <div className="bg-slate-700 px-5 py-3 rounded-t-2xl">
          <div className="flex items-center justify-between gap-4">
            <h3 className="text-base font-semibold text-white whitespace-nowrap flex-shrink-0">Checklists</h3>
            <div className="flex items-center gap-3 flex-1 justify-end">
              <div className="relative flex-1 max-w-xs">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
                <input
                  type="text"
                  placeholder="Search checklists..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-9 pr-3 py-2 bg-white text-slate-800 text-sm border border-white/20 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500 transition-all duration-200"
                />
              </div>
              <button
                onClick={() => setShowSettings(true)}
                className="p-2 rounded-lg hover:bg-slate-500 transition-colors"
                title="Checklist Settings"
              >
                <Settings className="h-5 w-5 text-white" />
              </button>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* FT-310: Subtab switcher — Categories (existing) | History (new).
              Both surfaces share the same Checklists card frame; only the
              inner body swaps. Keeps navigation shallow (no separate top-level
              Operations tab) and leaves the existing Recent strip on the
              Categories view untouched. */}
          <div
            role="tablist"
            aria-label="Checklists view"
            className="flex items-center gap-1 border-b border-slate-200"
          >
            <button
              role="tab"
              type="button"
              aria-selected={activeSubtab === 'categories'}
              onClick={() => setActiveSubtab('categories')}
              data-testid="checklists-subtab-categories"
              className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${
                activeSubtab === 'categories'
                  ? 'border-cyan-600 text-cyan-700'
                  : 'border-transparent text-slate-500 hover:text-slate-700'
              }`}
            >
              Categories
            </button>
            <button
              role="tab"
              type="button"
              aria-selected={activeSubtab === 'history'}
              onClick={() => setActiveSubtab('history')}
              data-testid="checklists-subtab-history"
              className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors flex items-center gap-1.5 ${
                activeSubtab === 'history'
                  ? 'border-cyan-600 text-cyan-700'
                  : 'border-transparent text-slate-500 hover:text-slate-700'
              }`}
            >
              <History className="h-4 w-4" />
              History
            </button>
          </div>

          {activeSubtab === 'history' ? (
            <ChecklistHistoryTab />
          ) : (
            <>
              {/* Category Cards Grid */}
              <div>
                <h3 className="text-sm font-medium text-slate-500 uppercase tracking-wide mb-3">
                  Categories
                </h3>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                  {categories.map(category => (
                    <CategoryCard
                      key={category.id}
                      category={category}
                      onClick={() => handleCategoryClick(category)}
                      statusBadge={getCategoryStatus(category.id)}
                    />
                  ))}
                  {/* Custom categories */}
                  {customCategories.map(category => (
                    <CategoryCard
                      key={category.id}
                      category={category}
                      onClick={() => handleCategoryClick(category)}
                      statusBadge={{ text: 'Custom', color: 'text-indigo-500' }}
                    />
                  ))}
                </div>
              </div>

              {/* FT-96: Quick Start buttons removed - they were redundant with category cards */}

              {/* Recent Completions */}
              {recentCompletions.length > 0 && (
                <div>
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="text-sm font-medium text-slate-500 uppercase tracking-wide">
                      Recent
                    </h3>
                    {/* FT-310: "View All" now jumps to the History subtab. */}
                    <button
                      type="button"
                      onClick={() => setActiveSubtab('history')}
                      className="text-sm text-cyan-600 hover:text-cyan-700 font-medium flex items-center gap-1"
                      data-testid="checklists-recent-view-all"
                    >
                      <History className="h-4 w-4" />
                      View All
                    </button>
                  </div>
                  <div className="bg-slate-50 rounded-xl divide-y divide-slate-200">
                    {recentCompletions.slice(0, 3).map(record => (
                      <RecentItem
                        key={record.id}
                        record={record}
                        onView={(r) => setViewingRecord(r)}
                      />
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* Daily Check Execution Modal */}
      {selectedDailyCheck && (
        <DailyCheckExecutionView
          category={selectedDailyCheck}
          onClose={() => setSelectedDailyCheck(null)}
          onComplete={(record) => {
            // FT-314: mirror DailyChecksQuickAccess — write to sr.daily_checks.v1
            // via completeCategory BEFORE logging to the Main Logbook. Without
            // this, the Dashboard "Daily Checks X/Y today" badge only updates
            // when checks are completed from the voyage-screen path; the primary
            // Operations → Checklists → Daily Check path used to skip it.
            const checkResults = record?.checks || selectedDailyCheck.checks.map(c => ({
              id: c.id,
              text: c.text,
              passed: true
            }));
            completeCategory(
              selectedDailyCheck.id,
              checkResults,
              record?.completedBy || { name: 'Crew Member' }
            );

            // FT-309: auto-log completion to Main Logbook so Checklists → Daily Check
            // completions surface on the logbook page (mirror DailyChecksQuickAccess path).
            logDailyCheck({
              category: selectedDailyCheck.id,
              categoryLabel: selectedDailyCheck.label,
              checksCount: record?.checks?.length || 0,
              checksPassed: record?.checks?.filter(c => c.passed).length || 0,
              checks: record?.checks || [],
              completedAt: record?.completedAt,
              completedBy: record?.completedBy,
              notes: record?.notes || ''
            });
            setSelectedDailyCheck(null);
          }}
        />
      )}

      {/* View Completed Record */}
      {viewingRecord && (
        <CompletedChecklistView
          record={viewingRecord}
          onClose={() => setViewingRecord(null)}
        />
      )}

      {/* Generic Checklist Execution View */}
      {executingChecklist && (
        <GenericChecklistExecutionView
          checklist={executingChecklist}
          onClose={() => setExecutingChecklist(null)}
          onComplete={(record) => {
            // FT-322: mirror FT-309's daily-check auto-log pattern for
            // non-daily checklists (Watch Handover, Heavy Weather, Anchoring…).
            // Dedupes against the inner call inside GenericChecklistExecutionView
            // via record.id → logbookEntries[].sourceId.
            autoLogNonDailyChecklistCompletion({
              record,
              logbookEntries,
              logChecklistCompletion
            });
            setExecutingChecklist(null);
          }}
        />
      )}
    </div>
  );
};

export default ChecklistsTab;
